import { useState } from "react";
import {
  Sparkles,
  Copy,
  Check,
  RotateCcw,
  Clock,
  Download,
  Loader2,
  Image as ImageIcon,
  Instagram,
  Facebook,
} from "lucide-react";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { HistoryDrawer, type HistoryDraft } from "./HistoryDrawer";

// ─── Types ────────────────────────────────────────────────────────────────────

type Platform = "instagram" | "facebook" | "tiktok";
type Tone = "professional" | "casual" | "bold";

interface DraftResult {
  platform: Platform;
  /** One or more on-brand variations of the post. */
  texts: string[];
}

// ─── Platform config ──────────────────────────────────────────────────────────

const PLATFORMS: { id: Platform; label: string; icon: React.ReactNode; color: string }[] = [
  {
    id: "instagram",
    label: "Instagram",
    icon: <Instagram className="h-4 w-4" />,
    color: "bg-gradient-to-br from-purple-500 to-pink-500",
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: <Facebook className="h-4 w-4" />,
    color: "bg-[#0866FF]",
  },
  {
    id: "tiktok",
    label: "TikTok",
    icon: <TikTokIcon />,
    color: "bg-[#111827]",
  },
];

const TONES: { id: Tone; label: string; hint: string }[] = [
  { id: "professional", label: "Professional", hint: "Polished, brand-forward" },
  { id: "casual", label: "Casual", hint: "Friendly, conversational" },
  { id: "bold", label: "Bold", hint: "Confident, attention-grabbing" },
];

const VARIANT_OPTIONS = [1, 2, 3] as const;

const PLATFORM_IDS = new Set<Platform>(["instagram", "facebook", "tiktok"]);

// ─── Draft card (one platform, N variants) ──────────────────────────────────────

function DraftCard({ result }: { result: DraftResult }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const platform = PLATFORMS.find((p) => p.id === result.platform)!;
  const text = result.texts[active] ?? "";
  const hasVariants = result.texts.length > 1;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-brown-100 bg-white">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-brown-50 px-5 py-3">
        <div
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-md text-white",
            platform.color,
          )}
        >
          {platform.icon}
        </div>
        <span className="font-display text-sm font-bold text-brown-500">
          {platform.label}
        </span>

        {/* Variant switcher */}
        {hasVariants && (
          <div className="ml-2 flex items-center gap-1">
            {result.texts.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                title={`Variation ${i + 1}`}
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded text-[11px] font-bold transition-colors",
                  active === i
                    ? "bg-peach-400 text-white"
                    : "bg-cream-100 text-brown-400 hover:bg-cream-200",
                )}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        <span className="ml-auto text-xs text-brown-300">{text.length} chars</span>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-brown-500">
          {text}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 border-t border-brown-50 px-5 py-3">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-600" />
              <span className="text-green-600">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
        {hasVariants && (
          <span className="ml-auto text-[11px] text-brown-300">
            Variation {active + 1} of {result.texts.length}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function SocialTab() {
  const [topic, setTopic] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(
    new Set(["instagram", "facebook", "tiktok"]),
  );
  const [tone, setTone] = useState<Tone>("professional");
  const [variants, setVariants] = useState<number>(3);
  const [drafts, setDrafts] = useState<DraftResult[]>([]);
  // Bumped whenever `drafts` is replaced (generate or history-load) so the
  // DraftCards remount and reset their active-variant index — otherwise a
  // stale index could point past a loaded draft's shorter variant list.
  const [resultNonce, setResultNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // AI product image (optional, generated from the topic). lastDraftId ties
  // the image back to the saved history row it belongs to.
  const [lastDraftId, setLastDraftId] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const togglePlatform = (p: Platform) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p) && next.size === 1) return prev;
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { drafts: raw, draftId } = await api<{
        drafts: Record<string, unknown>;
        draftId?: string;
      }>("/api/marketing/social/generate", {
        method: "POST",
        body: JSON.stringify({
          topic: topic.trim(),
          platforms: Array.from(selectedPlatforms),
          tone,
          variants,
        }),
      });
      const results: DraftResult[] = Object.entries(raw)
        .filter(([k]) => PLATFORM_IDS.has(k as Platform))
        .map(([k, v]) => ({
          platform: k as Platform,
          texts: Array.isArray(v) ? (v as string[]) : [String(v)],
        }));
      setDrafts(results);
      setResultNonce((n) => n + 1);
      // New generation → tie future image to this draft, clear any prior image.
      setLastDraftId(typeof draftId === "string" ? draftId : null);
      setImageUrl(null);
      setImageError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async () => {
    if (!topic.trim()) return;
    setImageLoading(true);
    setImageError(null);
    try {
      const { imageUrl: url } = await api<{ imageUrl?: string }>(
        "/api/marketing/social/image",
        {
          method: "POST",
          body: JSON.stringify({
            topic: topic.trim(),
            draftId: lastDraftId ?? undefined,
          }),
        },
      );
      setImageUrl(typeof url === "string" ? url : null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) {
        setImageError(
          "Image generation isn't set up yet — add REPLICATE_API_TOKEN in Railway.",
        );
      } else {
        setImageError(apiErrorMessage(e, "Image generation failed"));
      }
    } finally {
      setImageLoading(false);
    }
  };

  // Re-populate the form + drafts from a saved history entry.
  const loadFromHistory = (d: HistoryDraft) => {
    setTopic(d.topic);
    if (d.tone === "professional" || d.tone === "casual" || d.tone === "bold") {
      setTone(d.tone);
    }
    const inputs = (d.inputs ?? {}) as { platforms?: unknown };
    const platforms = Array.isArray(inputs.platforms)
      ? (inputs.platforms as string[]).filter((p): p is Platform =>
          PLATFORM_IDS.has(p as Platform),
        )
      : [];
    if (platforms.length) setSelectedPlatforms(new Set(platforms));

    const output = (d.output ?? {}) as Record<string, unknown>;
    const results: DraftResult[] = Object.entries(output)
      .filter(([k]) => PLATFORM_IDS.has(k as Platform))
      .map(([k, v]) => ({
        platform: k as Platform,
        texts: Array.isArray(v)
          ? (v as string[])
          : typeof v === "string"
            ? [v]
            : [],
      }));
    setDrafts(results);
    setResultNonce((n) => n + 1);
    setError(null);
    // Restore any image saved with this draft + tie future regenerations to it.
    setImageUrl(d.imageUrl ?? null);
    setLastDraftId(d.id);
    setImageError(null);
  };

  const totalVariants = drafts.reduce((n, d) => n + d.texts.length, 0);

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold text-brown-500">
            Social content
          </h2>
          <p className="mt-0.5 text-sm text-brown-400">
            Describe what you want to post and get on-brand drafts for every
            platform.
          </p>
        </div>
        <button
          onClick={() => setHistoryOpen(true)}
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" />
          History
        </button>
      </div>

      {/* Generation form */}
      <div className="rounded-lg border border-brown-100 bg-white p-6 space-y-5">
        {/* Topic */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
            What's this post about?
          </label>
          <textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. We just launched a new line of custom embroidered hats — perfect for corporate gifting season."
            rows={3}
            className="w-full rounded-md border border-brown-200 bg-cream-50 px-4 py-3 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none focus:ring-1 focus:ring-peach-400 resize-none"
          />
        </div>

        {/* Platform toggles */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
            Platforms
          </label>
          <div className="flex gap-2 flex-wrap">
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                onClick={() => togglePlatform(p.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors",
                  selectedPlatforms.has(p.id)
                    ? "border-peach-400 bg-peach-50 text-brown-500"
                    : "border-brown-200 bg-white text-brown-400 hover:border-brown-300",
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded text-white",
                    p.color,
                  )}
                >
                  {p.icon}
                </span>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tone + Variations */}
        <div className="flex flex-wrap gap-x-8 gap-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
              Tone
            </label>
            <div className="flex gap-2 flex-wrap">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTone(t.id)}
                  title={t.hint}
                  className={cn(
                    "rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors",
                    tone === t.id
                      ? "border-peach-400 bg-peach-50 text-brown-500"
                      : "border-brown-200 bg-white text-brown-400 hover:border-brown-300",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-brown-400 mb-2">
              Variations
            </label>
            <div className="flex gap-2">
              {VARIANT_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => setVariants(n)}
                  title={`${n} variation${n > 1 ? "s" : ""} per platform`}
                  className={cn(
                    "h-9 w-9 rounded-md border text-sm font-semibold transition-colors",
                    variants === n
                      ? "border-peach-400 bg-peach-50 text-brown-500"
                      : "border-brown-200 bg-white text-brown-400 hover:border-brown-300",
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Generate button */}
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={generate}
            disabled={!topic.trim() || loading}
            className={cn(
              "flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors",
              topic.trim() && !loading
                ? "bg-peach-400 text-white hover:bg-peach-500"
                : "bg-cream-200 text-brown-300 cursor-not-allowed",
            )}
          >
            <Sparkles className={cn("h-4 w-4", loading && "animate-pulse")} />
            {loading ? "Generating…" : "Generate drafts"}
          </button>

          {drafts.length > 0 && !loading && (
            <button
              onClick={generate}
              className="flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500 transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Regenerate
            </button>
          )}
        </div>

        {error && <p className="text-sm text-rust-600 font-medium">{error}</p>}
      </div>

      {/* Drafts */}
      {drafts.length > 0 && (
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-brown-400">
            Drafts — {drafts.length} platform{drafts.length > 1 ? "s" : ""}
            {totalVariants > drafts.length
              ? ` · ${totalVariants} variations`
              : ""}
          </p>

          {/* Post image (AI-generated, optional) */}
          <div className="rounded-lg border border-brown-100 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-ink underline underline-offset-4" />
                <span className="font-display text-sm font-bold text-brown-500">
                  Post image
                </span>
              </div>
              <div className="flex items-center gap-3">
                {imageUrl && (
                  <a
                    href={imageUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500 transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                )}
                <button
                  onClick={generateImage}
                  disabled={imageLoading}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors",
                    imageLoading
                      ? "bg-cream-200 text-brown-300 cursor-not-allowed"
                      : "bg-peach-400 text-white hover:bg-peach-500",
                  )}
                >
                  {imageLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {imageLoading
                    ? "Generating…"
                    : imageUrl
                      ? "Regenerate"
                      : "Generate image"}
                </button>
              </div>
            </div>

            {imageError && (
              <p className="mt-3 text-sm text-rust-600 font-medium">
                {imageError}
              </p>
            )}

            {imageLoading ? (
              <div className="mt-4 flex aspect-square w-full max-w-xs items-center justify-center rounded-md border border-dashed border-brown-200 bg-cream-50">
                <Loader2 className="h-6 w-6 animate-spin text-peach-400" />
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt="AI-generated social post image"
                className="mt-4 w-full max-w-xs rounded-md border border-brown-100"
              />
            ) : (
              <p className="mt-3 text-sm text-brown-400">
                Generate an on-brand image to pair with these posts.
              </p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {drafts.map((d) => (
              <DraftCard key={`${d.platform}-${resultNonce}`} result={d} />
            ))}
          </div>
        </div>
      )}

      <HistoryDrawer
        kind="social"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoad={loadFromHistory}
      />
    </div>
  );
}

// ─── TikTok icon ──────────────────────────────────────────────────────────────

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z" />
    </svg>
  );
}
